import React from "react";
import { Mail, Smartphone, Banknote, Copy, ShieldCheck, Truck, Heart, Sparkles } from "lucide-react";
import { useLang } from "../store";
import { ALT_PAYMENTS } from "../assets";

export default function AltPayments() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const [copied, setCopied] = React.useState("");

  const copy = (val, key) => {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 1600);
    });
  };

  const rows = [
    { key: "paypal", Icon: Mail, label: "PayPal", value: ALT_PAYMENTS.paypal_email },
    { key: "bizum", Icon: Smartphone, label: "Bizum", value: ALT_PAYMENTS.bizum },
    { key: "iban", Icon: Banknote, label: isEs ? "Transferencia (IBAN)" : "Bank transfer (IBAN)", value: ALT_PAYMENTS.iban },
  ];

  const trustItems = [
    {
      Icon: Truck,
      title: isEs ? "Envío en 24-48h" : "24-48h shipping",
      desc: isEs ? "Desde España, directo a tu puerta." : "From Spain, straight to your door.",
    },
    {
      Icon: ShieldCheck,
      title: isEs ? "Pago 100% seguro" : "100% secure payment",
      desc: isEs ? "Stripe, PayPal o transferencia. Tú eliges." : "Stripe, PayPal or transfer. Your choice.",
    },
    {
      Icon: Heart,
      title: isEs ? "Garantía de calidad" : "Quality guarantee",
      desc: isEs ? "Materiales food-grade certificados, sin BPA." : "Certified food-grade materials, BPA-free.",
    },
    {
      Icon: Sparkles,
      title: isEs ? "Hecho con cariño" : "Made with love",
      desc: isEs ? "Pequeña marca española. Cada pedido importa." : "Small Spanish brand. Every order matters.",
    },
  ];

  return (
    <section id="alt-pay" className="bg-[#FFCC00] border-b-[3px] border-[#0C0A09]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="nb-badge">{isEs ? "Otros pagos" : "Other payment methods"}</span>
          <h2 className="font-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
            {isEs ? "¿Sin tarjeta? No problem." : "No card? No problem."}
          </h2>
          <p className="mt-4 font-medium text-base md:text-lg">
            {isEs
              ? "Acepto PayPal, Bizum o transferencia. Tras pagar, envía el justificante a "
              : "I accept PayPal, Bizum or bank transfer. After paying, send the receipt to "}
            <a href={`mailto:${ALT_PAYMENTS.paypal_email}`} className="font-bold underline underline-offset-4 decoration-[3px]">
              {ALT_PAYMENTS.paypal_email}
            </a>
            {isEs ? " y confirmo el pedido en menos de 24h." : " and I'll confirm your order within 24h."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Trust / Why Velora card */}
          <div className="lg:col-span-5">
            <div
              className="bg-white border-[3px] border-[#0C0A09] shadow-[8px_8px_0_0_#0C0A09] p-6 md:p-8"
              data-testid="trust-card"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-[#9BE800] border-[3px] border-[#0C0A09] flex items-center justify-center">
                  <ShieldCheck size={18} strokeWidth={2.75} />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">
                    {isEs ? "Por qué Velora" : "Why Velora"}
                  </div>
                  <div className="font-display text-2xl leading-none">
                    {isEs ? "Confía en nuestro trabajo" : "Trust our work"}
                  </div>
                </div>
              </div>

              <p className="font-medium text-sm md:text-base leading-relaxed mb-5">
                {isEs
                  ? "Cada cup pasa controles de calidad antes de salir de nuestro almacén. Si algo no te convence, escríbenos — siempre respondemos."
                  : "Every cup passes quality checks before leaving our warehouse. If something's not right, write to us — we always reply."}
              </p>

              <div className="space-y-3">
                {trustItems.map(({ Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3 bg-[#FAF9F6] border-[3px] border-[#0C0A09] p-3">
                    <div className="w-10 h-10 rounded-full bg-[#FFCC00] border-[3px] border-[#0C0A09] flex items-center justify-center flex-shrink-0">
                      <Icon size={16} strokeWidth={2.75} />
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg leading-tight">{title}</div>
                      <div className="text-xs font-medium opacity-75 leading-snug mt-0.5">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 bg-[#0C0A09] text-white border-[3px] border-[#0C0A09] p-4 text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9BE800] mb-1">
                  {isEs ? "Contáctanos" : "Contact us"}
                </div>
                <a
                  href={`mailto:${ALT_PAYMENTS.paypal_email}`}
                  className="font-display text-lg md:text-xl underline underline-offset-4 decoration-[2px] hover:text-[#9BE800] transition-colors break-all"
                  data-testid="trust-contact-email"
                >
                  {ALT_PAYMENTS.paypal_email}
                </a>
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="lg:col-span-7 space-y-3">
            {rows.map(({ key, Icon, label, value }) => (
              <div key={key} className="bg-white border-[3px] border-[#0C0A09] shadow-[5px_5px_0_0_#0C0A09] p-4 md:p-5 flex items-center gap-4" data-testid={`altpay-${key}`}>
                <div className="w-12 h-12 rounded-full bg-[#9BE800] border-[3px] border-[#0C0A09] flex items-center justify-center flex-shrink-0">
                  <Icon size={20} strokeWidth={2.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-70">{label}</div>
                  <div className="font-display text-xl md:text-2xl leading-none mt-1 truncate">{value}</div>
                </div>
                <button
                  onClick={() => copy(value, key)}
                  data-testid={`altpay-copy-${key}`}
                  className="px-3 py-2 border-[3px] border-[#0C0A09] bg-[#FAF9F6] text-xs font-bold uppercase tracking-widest rounded-full hover:-translate-y-0.5 transition-transform flex items-center gap-1.5"
                >
                  <Copy size={13} strokeWidth={3} /> {(() => {
                    const isCopied = copied === key;
                    if (isEs) return isCopied ? "Copiado" : "Copiar";
                    return isCopied ? "Copied" : "Copy";
                  })()}
                </button>
              </div>
            ))}

            <div className="bg-[#0C0A09] text-white border-[3px] border-[#0C0A09] p-5 mt-2">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#9BE800] mb-2">
                {isEs ? "Cómo funciona" : "How it works"}
              </div>
              <ol className="text-sm space-y-1.5 font-medium leading-relaxed list-decimal list-inside">
                <li>{isEs ? "Elige tu método: PayPal, Bizum o transferencia." : "Pick a method: PayPal, Bizum or bank transfer."}</li>
                <li>{isEs ? "Copia los datos y envíame el pago." : "Copy the details and send me the payment."}</li>
                <li>{isEs ? "Mándame tu dirección de envío al email." : "Email me your shipping address."}</li>
                <li>{isEs ? "Te envío el pedido en menos de 24h." : "Order ships within 24h."}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
